import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Shell, StatBox } from "@/components/Shell";
import {
  getGameAsHost,
  updateGame,
  listTeams,
  listGamePurchases,
  addTest,
  updateTest,
  deleteTest,
  revealResult,
  gradeTeam,
  fmtMoney,
} from "@/lib/api";
import Leaderboard from "@/components/Leaderboard";

const TABS = ["Overview", "Case & Catalog", "Live Dashboard", "Reveal"];

export default function HostGame() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const urlToken = params.get("t");
  const storedToken = typeof window !== "undefined" ? localStorage.getItem(`host_${id}`) : null;
  const [token, setToken] = useState(urlToken || storedToken || "");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");
  const [tokenInput, setTokenInput] = useState("");

  const [game, setGame] = useState(null);
  const [teams, setTeams] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [tab, setTab] = useState("Overview");

  const refresh = async (tk = token) => {
    if (!tk) return;
    try {
      const [g, t, p] = await Promise.all([
        getGameAsHost(id, tk),
        listTeams(id),
        listGamePurchases(id),
      ]);
      setGame(g);
      setTeams(t);
      setPurchases(p);
      if (!authed) {
        setAuthed(true);
        localStorage.setItem(`host_${id}`, tk);
        // Sync URL so refresh keeps working (but strip from address bar visually via replace)
        if (!urlToken) setParams({ t: tk }, { replace: true });
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        setAuthed(false);
        setAuthError("Invalid host token for this game.");
        localStorage.removeItem(`host_${id}`);
      } else if (err?.response?.status === 404) {
        setAuthError("Game not found.");
      }
    }
  };

  useEffect(() => {
    refresh();
    const int = setInterval(() => refresh(), 3000);
    return () => clearInterval(int);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  if (!authed) {
    return (
      <Shell>
        <div className="max-w-md" data-testid="host-locked">
          <div className="font-mono-data text-[10px] tracking-[0.3em] text-[color:var(--dxc-signal)] uppercase">
            Locked / Host Only
          </div>
          <h1 className="font-serif-display text-5xl mt-2">Host token required</h1>
          <p className="text-white/60 mt-3 text-sm">
            Only the person who created this game can open the console. Paste your
            host token or open your Host Link (contains <span className="font-mono-data">?t=…</span>).
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!tokenInput.trim()) return;
              setAuthError("");
              setToken(tokenInput.trim());
            }}
            className="dxc-card p-6 mt-6"
          >
            <label className="font-mono-data text-[10px] tracking-widest text-white/60 uppercase">
              Host Token
            </label>
            <input
              data-testid="host-token-input"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="paste token…"
              className="dxc-input mt-2 font-mono-data text-sm"
              autoFocus
            />
            {authError && (
              <div
                data-testid="host-token-error"
                className="mt-3 font-mono-data text-[10px] tracking-widest text-[color:var(--dxc-signal)] uppercase"
              >
                {authError}
              </div>
            )}
            <button
              data-testid="host-token-submit"
              className="dxc-btn dxc-btn-volt w-full mt-4 text-xs"
            >
              Unlock Console →
            </button>
          </form>

          <Link
            to="/"
            className="mt-6 inline-block text-xs text-white/50 hover:text-white font-mono-data tracking-widest"
          >
            ← Back
          </Link>
        </div>
      </Shell>
    );
  }

  if (!game) {
    return (
      <Shell>
        <div className="font-mono-data text-sm text-white/60" data-testid="host-loading">
          Loading session…
        </div>
      </Shell>
    );
  }

  const totalSpent = purchases.reduce((s, p) => s + p.cost, 0);

  return (
    <Shell
      right={
        <div className="flex items-center gap-3">
          <span className="font-mono-data text-[10px] tracking-widest text-white/50">CODE</span>
          <span
            data-testid="host-game-code"
            className="font-mono-data text-2xl tracking-[0.35em] text-[color:var(--dxc-volt)] border border-white/20 px-3 py-1"
          >
            {game.game_code}
          </span>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 mb-8 border border-white/15">
        <StatBox label="Session" value={game.title || "Untitled"} tone="default" mono={false} testId="stat-title" />
        <div className="border-l border-white/15">
          <StatBox
            label="Teams"
            value={teams.length}
            tone="volt"
            testId="stat-teams"
          />
        </div>
        <div className="border-l border-white/15">
          <StatBox
            label="Tests Bought"
            value={purchases.length}
            testId="stat-purchases"
          />
        </div>
        <div className="border-l border-white/15">
          <StatBox
            label="Total Spent"
            value={fmtMoney(totalSpent)}
            tone="red"
            testId="stat-total-spent"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-0 border-b border-white/15 mb-8">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`host-tab-${t.replace(/[^a-z]/gi, "").toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`px-5 py-3 font-mono-data text-[11px] tracking-[0.25em] uppercase border-r border-white/15 transition-colors duration-100 ${
              tab === t
                ? "bg-white text-black"
                : "text-white/70 hover:bg-white/5"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && <OverviewTab game={game} refresh={refresh} />}
      {tab === "Case & Catalog" && <CatalogTab game={game} refresh={refresh} />}
      {tab === "Live Dashboard" && <LiveTab game={game} teams={teams} />}
      {tab === "Reveal" && (
        <RevealTab
          game={game}
          teams={teams}
          purchases={purchases}
          refresh={refresh}
        />
      )}
    </Shell>
  );
}

// ---------------- Overview ----------------
const OverviewTab = ({ game, refresh }) => {
  const [status, setStatus] = useState(game.status);

  const cycle = async (s) => {
    setStatus(s);
    await updateGame(game.id, { status: s });
    toast.success(`Status: ${s.toUpperCase()}`);
    refresh();
  };

  const join_url = `${window.location.origin}/`;
  const host_url = `${window.location.origin}/host/game/${game.id}?t=${game.host_token}`;

  const copyHostLink = async () => {
    try {
      await navigator.clipboard.writeText(host_url);
      toast.success("Host link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 dxc-card p-8">
        <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
          Session Code — share with residents
        </div>
        <div
          data-testid="overview-code"
          className="font-mono-data text-6xl md:text-7xl tracking-[0.25em] mt-3 text-[color:var(--dxc-volt)]"
        >
          {game.game_code}
        </div>
        <div className="mt-4 text-white/60 text-sm">
          Join URL: <span className="font-mono-data text-white">{join_url}</span>
        </div>

        <div className="mt-6 pt-6 border-t border-white/10" data-testid="host-link-box">
          <div className="flex items-baseline justify-between gap-3">
            <div className="font-mono-data text-[10px] tracking-[0.3em] text-[color:var(--dxc-signal)] uppercase">
              Host Link — keep private
            </div>
            <button
              onClick={copyHostLink}
              data-testid="copy-host-link"
              className="dxc-btn text-[10px] py-1 px-3"
            >
              Copy
            </button>
          </div>
          <div className="mt-2 font-mono-data text-[11px] text-white/70 break-all border border-white/10 bg-black/40 p-3">
            {host_url}
          </div>
          <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase mt-2">
            Anyone with this link can control the game. Bookmark it — you're already signed in on this browser.
          </div>
        </div>

        <div className="mt-8 border-t border-white/10 pt-6">
          <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase mb-3">
            Session State
          </div>
          <div className="flex gap-0 border border-white/15 w-fit">
            {["open", "reveal", "closed"].map((s) => (
              <button
                key={s}
                data-testid={`status-btn-${s}`}
                onClick={() => cycle(s)}
                className={`px-5 py-2 font-mono-data text-[11px] tracking-[0.25em] uppercase border-r border-white/15 last:border-r-0 transition-colors duration-100 ${
                  status === s
                    ? "bg-[color:var(--dxc-volt)] text-black"
                    : "text-white/70 hover:bg-white/5"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="mt-3 text-white/50 text-xs font-mono-data">
            OPEN = teams can join & buy · REVEAL = show all results · CLOSED = frozen
          </div>
        </div>
      </div>

      <div className="dxc-card p-8">
        <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
          Patient
        </div>
        <div className="font-serif-display text-2xl mt-2">{game.case_chief_complaint || "No chief complaint set"}</div>
        <div className="mt-6 space-y-4 text-sm">
          <Row k="History" v={game.case_history} />
          <Row k="Vitals" v={game.case_vitals} mono />
          <Row k="Exam" v={game.case_physical_exam} />
          <Row k="Dx (private)" v={game.correct_diagnosis} highlight />
        </div>
      </div>
    </div>
  );
};

const Row = ({ k, v, mono, highlight }) => (
  <div>
    <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase">
      {k}
    </div>
    <div
      className={`mt-1 whitespace-pre-wrap ${mono ? "font-mono-data text-xs" : ""} ${
        highlight ? "text-[color:var(--dxc-volt)]" : "text-white/80"
      }`}
    >
      {v || <span className="text-white/30">—</span>}
    </div>
  </div>
);

// ---------------- Catalog ----------------
const CatalogTab = ({ game, refresh }) => {
  const [name, setName] = useState("");
  const [cat, setCat] = useState("Laboratory");
  const [cost, setCost] = useState(100);
  const [resultTpl, setResultTpl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !cost) return;
    await addTest(game.id, {
      name: name.trim(),
      category: cat,
      cost: Number(cost),
      result_template: resultTpl,
    });
    setName("");
    setCost(100);
    setResultTpl("");
    toast.success("Test added");
    refresh();
  };

  const remove = async (testId) => {
    await deleteTest(game.id, testId);
    toast.success("Removed");
    refresh();
  };

  const editCost = async (testId, newCost) => {
    await updateTest(game.id, testId, { cost: Number(newCost) });
    refresh();
  };

  const editResult = async (testId, newResult) => {
    await updateTest(game.id, testId, { result_template: newResult });
    refresh();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={submit} className="dxc-card p-6" data-testid="add-test-form">
        <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
          Add / Custom Test
        </div>
        <h3 className="font-serif-display text-2xl mt-1 mb-4">New line item</h3>
        <input
          data-testid="new-test-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Test name"
          className="dxc-input mb-3"
        />
        <select
          data-testid="new-test-cat"
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="dxc-input mb-3"
        >
          <option>Laboratory</option>
          <option>Imaging</option>
          <option>Procedure</option>
          <option>Consult</option>
        </select>
        <input
          data-testid="new-test-cost"
          type="number"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          placeholder="Cost ($)"
          className="dxc-input mb-3 font-mono-data"
        />
        <textarea
          data-testid="new-test-result"
          value={resultTpl}
          onChange={(e) => setResultTpl(e.target.value)}
          placeholder="Result template (optional — teams see this on reveal)"
          className="dxc-input mb-3 min-h-[90px]"
        />
        <button data-testid="add-test-submit" className="dxc-btn dxc-btn-volt w-full text-xs">
          Add Test →
        </button>
      </form>

      <div className="lg:col-span-2 dxc-card p-0" data-testid="catalog-table">
        <div className="p-6 border-b border-white/10 flex justify-between items-baseline">
          <div>
            <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
              Catalog
            </div>
            <h3 className="font-serif-display text-2xl mt-1">
              {game.tests.length} tests available
            </h3>
            <div className="font-mono-data text-[10px] tracking-widest text-[color:var(--dxc-volt)]/80 uppercase mt-2">
              Result templates auto-reveal to teams on purchase · edit any purchase result live from the Reveal tab
            </div>
          </div>
        </div>
        <div className="max-h-[600px] overflow-auto">
          {game.tests.map((t, i) => (
            <div
              key={t.id}
              data-testid={`catalog-row-${t.id}`}
              className={`grid grid-cols-12 gap-3 px-6 py-3 items-center ${
                i !== 0 ? "border-t border-white/8" : ""
              } hover:bg-white/[0.02]`}
            >
              <div className="col-span-6">
                <div className="text-sm">{t.name}</div>
                <div className="font-mono-data text-[10px] text-white/50 mt-0.5 uppercase tracking-widest">
                  {t.category}
                </div>
              </div>
              <div className="col-span-3">
                <input
                  data-testid={`edit-cost-${t.id}`}
                  type="number"
                  defaultValue={t.cost}
                  onBlur={(e) => e.target.value != t.cost && editCost(t.id, e.target.value)}
                  className="dxc-input py-1 font-mono-data text-sm"
                />
              </div>
              <div className="col-span-3 flex gap-2 justify-end">
                <button
                  data-testid={`del-test-${t.id}`}
                  onClick={() => remove(t.id)}
                  className="dxc-btn text-[10px] py-1 px-3 hover:!bg-[color:var(--dxc-signal)] hover:!text-white hover:!border-[color:var(--dxc-signal)]"
                >
                  Delete
                </button>
              </div>
              <div className="col-span-12 mt-1">
                <textarea
                  data-testid={`result-tpl-${t.id}`}
                  defaultValue={t.result_template}
                  onBlur={(e) =>
                    e.target.value !== (t.result_template || "") &&
                    editResult(t.id, e.target.value)
                  }
                  placeholder="Default result to reveal when this test is purchased (optional)"
                  className="dxc-input py-1 text-xs min-h-[36px]"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------- Live Dashboard ----------------
const LiveTab = ({ game, teams }) => {
  const sorted = [...teams].sort((a, b) => b.budget_remaining - a.budget_remaining);
  return (
    <div>
      <div className="mb-6 flex justify-between items-baseline">
        <div>
          <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
            Live Dashboard / Auto-refresh 3s
          </div>
          <h2 className="font-serif-display text-4xl mt-1">Team standings</h2>
          <p className="text-white/50 text-sm mt-1 font-mono-data">
            Budgets only — test lists hidden until REVEAL phase
          </p>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="dxc-card p-10 text-center text-white/50 font-mono-data text-sm">
          Waiting for teams to join…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-white/15">
          {sorted.map((t, i) => {
            const pct = (t.budget_remaining / game.starting_budget) * 100;
            const tone =
              pct > 70 ? "var(--dxc-volt)" : pct > 30 ? "#fff" : "var(--dxc-signal)";
            return (
              <div
                key={t.id}
                data-testid={`live-team-${t.id}`}
                className={`p-6 relative ${
                  i % 4 !== 3 ? "md:border-r border-white/15" : ""
                } ${Math.floor(i / 4) > 0 ? "border-t border-white/15" : ""}`}
              >
                <div className="flex justify-between items-baseline">
                  <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase">
                    Rank {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-mono-data text-[10px] text-white/40 uppercase tracking-widest">
                    {t.team_code}
                  </div>
                </div>
                <div className="font-serif-display text-2xl mt-2 leading-tight truncate">
                  {t.name}
                </div>
                <div className="mt-6 font-mono-data text-4xl" style={{ color: tone }}>
                  {fmtMoney(t.budget_remaining)}
                </div>
                <div className="mt-2 h-1 bg-white/10 relative">
                  <div
                    className="absolute top-0 left-0 h-full"
                    style={{ width: `${pct}%`, background: tone }}
                  />
                </div>
                <div className="mt-3 flex justify-between font-mono-data text-[10px] text-white/40 uppercase tracking-widest">
                  <span>Spent {fmtMoney(game.starting_budget - t.budget_remaining)}</span>
                  <span>{pct.toFixed(0)}%</span>
                </div>
                {t.diagnosis_submitted && (
                  <div className="mt-4 pt-3 border-t border-white/10">
                    <div className="font-mono-data text-[10px] tracking-widest text-[color:var(--dxc-volt)] uppercase">
                      Dx Submitted
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------- Reveal ----------------
const RevealTab = ({ game, teams, purchases, refresh }) => {
  const byTeam = useMemo(() => {
    const m = {};
    for (const p of purchases) {
      (m[p.team_id] ||= []).push(p);
    }
    return m;
  }, [purchases]);

  const doReveal = async (purchaseId, txt) => {
    await revealResult(purchaseId, txt);
    toast.success("Result revealed");
    refresh();
  };

  const doGrade = async (teamId, grade) => {
    await gradeTeam(teamId, grade);
    toast.success(`Graded: ${grade.toUpperCase()}`);
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="reveal-panel">
      <Leaderboard teams={teams} startingBudget={game.starting_budget} testId="host-leaderboard" />

      <div>
        <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
          Final Reveal
        </div>
        <h2 className="font-serif-display text-4xl mt-1">Every team. Every order.</h2>
        <div className="mt-2 text-white/60 text-sm">
          Correct diagnosis:{" "}
          <span className="text-[color:var(--dxc-volt)] font-mono-data">
            {game.correct_diagnosis || "—"}
          </span>
        </div>
      </div>

      {teams.length === 0 && (
        <div className="dxc-card p-8 text-white/50">No teams yet.</div>
      )}

      {teams
        .slice()
        .sort((a, b) => b.budget_remaining - a.budget_remaining)
        .map((t) => {
          const list = byTeam[t.id] || [];
          const spent = list.reduce((s, p) => s + p.cost, 0);
          return (
            <div key={t.id} className="dxc-card p-6" data-testid={`reveal-team-${t.id}`}>
              <div className="flex flex-wrap justify-between items-baseline gap-4 pb-4 border-b border-white/10">
                <div>
                  <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase">
                    Team · {t.team_code}
                  </div>
                  <h3 className="font-serif-display text-3xl mt-1">{t.name}</h3>
                </div>
                <div className="flex gap-6">
                  <div>
                    <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase">
                      Remaining
                    </div>
                    <div className="font-mono-data text-2xl text-[color:var(--dxc-volt)]">
                      {fmtMoney(t.budget_remaining)}
                    </div>
                  </div>
                  <div>
                    <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase">
                      Spent
                    </div>
                    <div className="font-mono-data text-2xl text-[color:var(--dxc-signal)]">
                      {fmtMoney(spent)}
                    </div>
                  </div>
                  <div>
                    <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase">
                      Diagnosis
                    </div>
                    <div className="text-sm mt-1 max-w-[280px]">
                      {t.diagnosis_submitted || <span className="text-white/30">— not submitted</span>}
                    </div>
                    {t.diagnosis_submitted && (
                      <div className="flex gap-0 border border-white/20 mt-2 w-fit" data-testid={`grade-buttons-${t.id}`}>
                        {["correct", "partial", "incorrect", "none"].map((g) => {
                          const active = (t.diagnosis_grade || "none") === g;
                          const bg =
                            g === "correct"
                              ? "var(--dxc-volt)"
                              : g === "partial"
                                ? "#facc15"
                                : g === "incorrect"
                                  ? "var(--dxc-signal)"
                                  : "#fff";
                          return (
                            <button
                              key={g}
                              data-testid={`grade-${g}-${t.id}`}
                              onClick={() => doGrade(t.id, g)}
                              className="px-2 py-1 font-mono-data text-[9px] tracking-widest uppercase border-r border-white/20 last:border-r-0 transition-colors duration-100"
                              style={{
                                background: active ? bg : "transparent",
                                color: active ? "#050505" : "#fff",
                              }}
                            >
                              {g === "none" ? "clear" : g}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {list.length === 0 ? (
                <div className="text-white/40 py-6 font-mono-data text-xs uppercase tracking-widest">
                  No tests purchased
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {list.map((p) => (
                    <div
                      key={p.id}
                      className="grid grid-cols-12 gap-4 items-start py-2 border-b border-white/5"
                    >
                      <div className="col-span-5">
                        <div className="text-sm">{p.test_name}</div>
                        <div className="font-mono-data text-[10px] text-white/40 uppercase tracking-widest">
                          {p.test_category} · {new Date(p.purchased_at).toLocaleTimeString()}
                        </div>
                      </div>
                      <div className="col-span-2 font-mono-data text-[color:var(--dxc-signal)]">
                        -{fmtMoney(p.cost)}
                      </div>
                      <div className="col-span-5">
                        <textarea
                          data-testid={`reveal-result-${p.id}`}
                          defaultValue={p.result}
                          onBlur={(e) =>
                            e.target.value !== p.result && doReveal(p.id, e.target.value)
                          }
                          placeholder="Type result to reveal to team…"
                          className={`dxc-input py-1 text-xs min-h-[40px] ${
                            p.result_revealed ? "border-[color:var(--dxc-volt)]" : ""
                          }`}
                        />
                        {p.result_revealed && (
                          <div className="font-mono-data text-[9px] text-[color:var(--dxc-volt)] uppercase tracking-widest mt-1">
                            Revealed
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};
